import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import type { InfoProps, InfoColor } from './Info.types';

/**
 * Componente Info del sistema de diseño Siesa
 * 
 * Se utiliza para mostrar mensajes informativos, alertas inline o banners de estado.
 * Soporta múltiples esquemas de color y modo oscuro (dark mode).
 * 
 * @see docs/colors.md - Sistema de colores
 * @see docs/typography.md - Sistema de tipografía
 * 
 * @example
 * ```tsx
 * <Info 
 *   title="Información de cuenta" 
 *   description="Tu suscripción se renovará automáticamente el próximo mes." 
 *   color="blue" 
 * />
 * ```
 */
export const Info: React.FC<InfoProps> = ({
  title,
  description,
  color = 'zinc',
  icon,
  className = '',
  children,
  ...props
}) => {
  // ===== CONFIGURACIÓN DE COLORES =====
  const colorSchemes: Record<InfoColor, string> = {
    zinc: `
      bg-[#f4f4f5] border-[#d4d4d8] text-[#3f3f46]
      dark:bg-[#18181b] dark:border-[#3f3f46] dark:text-[#f4f4f5]
    `,
    blue: `
      bg-[#dbeefe] border-[#93d1fd] text-[#0e79fd]
      dark:bg-[#112d57] dark:border-[#0f6ae3] dark:text-[#93d1fd]
    `,
    red: `
      bg-[#fef2f2] border-[#fecaca] text-[#b91c1c]
      dark:bg-[#450a0a] dark:border-[#991b1b] dark:text-[#fecaca]
    `,
    green: `
      bg-[#f0fdf4] border-[#bbf7d0] text-[#15803d]
      dark:bg-[#064e3b] dark:border-[#065f46] dark:text-[#bbf7d0]
    `,
    yellow: `
      bg-[#fffbeb] border-[#fde68a] text-[#b45309]
      dark:bg-[#451a03] dark:border-[#78350f] dark:text-[#fde68a]
    `,
    orange: `
      bg-[#fff7ed] border-[#fed7aa] text-[#c2410c]
      dark:bg-[#431407] dark:border-[#9a3412] dark:text-[#fed7aa]
    `,
    purple: `
      bg-[#faf5ff] border-[#e9d5ff] text-[#7e22ce]
      dark:bg-[#3b0764] dark:border-[#581c87] dark:text-[#e9d5ff]
    `,
    primary: `
      bg-primary-100 border-primary-300 text-primary-600
      dark:bg-dark-bg-custom dark:border-dark-border-custom dark:text-dark-content-custom
    `,
    secondary: `
      bg-background-secondary border-border-primary text-content-primary
      dark:bg-dark-bg-primary dark:border-dark-border-primary dark:text-dark-content-primary
    `,
  };

  // ===== CLASES BASE =====
  const baseClasses = `
    flex
    items-start
    gap-3
    p-4
    rounded-lg
    border
    transition-colors
    duration-150
    w-full
  `;

  // ===== COMBINAR CLASES =====
  const finalClasses = [
    baseClasses,
    colorSchemes[color],
    className,
  ]
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  return (
    <div className={finalClasses} {...props}>
      {/* Icono */}
      <div className="shrink-0 mt-0.5">
        {icon || <InformationCircleIcon className="w-6 h-6" />}
      </div>

      {/* Contenedor de Texto */}
      <div className="flex flex-col gap-1 w-full">
        <h3 className="text-[18px] font-bold leading-[1.2] tracking-tight">
          {title}
        </h3>
        {description && (
          <p className="text-sm font-normal leading-[1.4] opacity-90">
            {description}
          </p>
        )}
        {children && <div className="mt-2">{children}</div>}
      </div>
    </div>
  );
};
